<template>
  <div class="asc-plot-manager">
    <div class="asc-plot-manager--close-button-container">
      <template v-if="isCloseButtonVisible">
        <div class="asc-plot-manager--close-button">
          <asc-tooltip left tooltip="Remove From Plot Editor" is-icon-tooltip>
            <asc-accent-button
              :on-click="remove"
              :disabled="associating || saving"
              button-text="Remove From Plot Editor"
              icon="mdi-close"
              icon-only
              secondary
              custom-class="asc-plot-manager--v-btn"
            ></asc-accent-button>
          </asc-tooltip>
        </div>
      </template>
    </div>
    <div
      :class="[
        'asc-plot-manager--container',
        isCloseButtonVisible
          ? 'asc-plot-manager--container-close-button'
          : null,
      ]"
    >
      <div class="asc-plot-manager--plot-name">
        <v-text-field
          v-model="model"
          :maxlength="50"
          class="compact"
          :color="greenColor"
          :disabled="!editable || titleDisabled"
          hide-details
          placeholder="Search"
          label="Plot Name"
          @input="debounceInput"
          @keydown="keyDown"
        ></v-text-field>
      </div>

      <div
        v-if="isDuplicateButtonVisible"
        class="asc-plot-manager--actions-container"
      >
        <asc-tooltip bottom tooltip="Duplicate This Plot" is-icon-tooltip>
          <asc-accent-button
            :on-click="duplicate"
            :disabled="associating || saving"
            button-text="Duplicate This Plot"
            icon="mdi-plus-circle-multiple-outline"
            icon-only
            secondary
          ></asc-accent-button>
        </asc-tooltip>
      </div>
      <div
        v-if="
          isAssociateButtonVisible &&
          !isMxRecEditPermission &&
          allowAddToIssue &&
          flightDataAttachPlotToMxRecPermission
        "
        class="asc-plot-manager--actions-container"
      >
        <asc-tooltip
          bottom
          tooltip="Add Plot to Mx &amp; Alerts"
          is-icon-tooltip
          :tooltip-length="0"
        >
          <asc-accent-button
            :on-click="addToIssue"
            :loading="associating"
            :disabled="associating || saving || associated"
            button-text="Add Plot to Mx & Alerts"
            icon="mdi-alert-circle"
            icon-only
            secondary
          ></asc-accent-button>
        </asc-tooltip>
      </div>
      <div v-if="editable" class="asc-plot-manager--save-container">
        <asc-accent-button
          button-text="Save"
          :on-click="save"
          :loading="saving"
          :disabled="isSaveButtonDisabled || tabsInvalid"
        ></asc-accent-button>
      </div>
    </div>
  </div>
</template>

<script>
import AscAccentButton from '../atoms/AscAccentButton'
import AscTooltip from '../atoms/AscTooltip'
import variables from '@/styles/_varsJs.scss'
import { debounceShortTime } from '@/utilities/appConsts'
import _ from 'lodash'
import { AscMxRecPermissionMixin } from '@/components/mixins'

export default {
  components: {
    AscAccentButton,
    AscTooltip,
  },
  mixins: [AscMxRecPermissionMixin],
  props: {
    allowAddToIssue: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: '',
    },

    flightDataAttachPlotToMxRecPermission: {
      type: Boolean,
      default: true,
    },
    mxRecPlot: {
      type: Boolean,
      default: false,
    },
    associating: {
      type: Boolean,
      default: false,
    },
    associated: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    actionButtons: {
      type: Boolean,
      default: true,
    },
    isCloseButtonVisible: {
      type: Boolean,
      default: true,
    },
    isDuplicateButtonVisible: {
      type: Boolean,
      default: true,
    },
    isAssociateButtonVisible: {
      type: Boolean,
      default: true,
    },

    titleDisabled: {
      type: Boolean,
      default: false,
    },
    onDuplicate: {
      type: Function,
      default: () => {},
    },
    onAddToIssue: {
      type: Function,
      default: () => {},
    },
    onRemove: {
      type: Function,
      default: () => {},
    },
    onSave: {
      type: Function,
      default: () => {},
    },
    tabsInvalid: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      grayColor: variables.gray400,
      greenColor: variables.accent500,
      viewModel: this.value,
    }
  },

  computed: {
    model: {
      get() {
        return this.viewModel
      },
      set(newValue) {
        this.viewModel = newValue
        this.$emit('update:value', newValue)
        this.$emit('input', newValue)
      },
    },

    anyActionButtonsVisible() {
      return (
        this.isCloseButtonVisible ||
        this.isDuplicateButtonVisible ||
        this.isAssociateButtonVisible
      )
    },

    isSaveButtonDisabled() {
      if (this.editable) {
        if (this.mxRecPlot) {
          return false
        }

        return this.associating || this.saving || this.associated
      }

      return false
    },
  },

  watch: {
    value(newValue) {
      this.viewModel = newValue
    },
  },

  methods: {
    debounceInput: _.debounce(function (e) {
      this.model = e
    }, debounceShortTime),

    keyDown(event) {
      if (![190].includes(event.keyCode)) return true
      event.preventDefault()
    },

    duplicate() {
      this.onDuplicate()
    },

    addToIssue() {
      this.onAddToIssue()
    },

    remove() {
      this.onRemove()
    },

    save() {
      this.onSave()
    },
  },
}
</script>

<style lang="scss">
.asc-plot-manager {
  .asc-plot-manager--close-button-container {
    position: relative;

    .asc-plot-manager--close-button {
      position: absolute;
      right: 0;
      top: 0;

      .asc-plot-manager--v-btn {
        border: 0;
        background-color: $gray-200 !important;
        min-width: 28px;
        min-height: 28px;
        width: 28px;
        height: 28px;

        &:hover {
          background-color: $accent-300 !important;
        }
      }
    }
  }

  .asc-plot-manager--container {
    padding: 16px;
    display: flex;
    width: 100%;

    .asc-plot-manager--plot-name {
      flex: 1;
      padding: 10px 8px 0 4px;

      .v-input {
        padding-top: 0;

        &.v-input--is-focused:not(.v-input--is-disabled) input {
          color: $primary-black;
        }

        &.v-input--is-disabled input {
          color: $primary-black;
        }

        &.v-input--is-disabled label {
          color: $primary-black;
        }
      }

      .v-input__slot::before {
        border-color: $gray-400 !important;
      }

      .v-text-field input {
        padding: 5px 0 0;
        font-size: 14px;
        line-height: 18px;

        .v-input__append-inner::after {
          background-color: $gray-400 !important;
        }
      }
    }

    .asc-plot-manager--actions-container {
      padding-left: 8px;
      padding-top: 6px;
      max-width: 44px;
      flex: 0 0 30%;
    }

    .asc-plot-manager--save-container {
      padding-left: 8px;
      padding-top: 6px;
      max-width: 80px;
      flex: 0 0 30%;
    }
  }

  .asc-plot-manager--container-close-button {
    padding: 16px 48px 16px 16px;
  }
}
</style>
